import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Check, Loader2, CloudCheck, CloudOff, AlertCircle } from "lucide-react";
import { WIZARD_STEPS } from "./constants";
import { emptyForm, hydrateForm, toApiPayload, validateStep } from "./formState";
import { useAutosave } from "./useAutosave";

import Step1Basic from "./steps/Step1Basic";
import Step2Appearance from "./steps/Step2Appearance";
import Step3Gallery from "./steps/Step3Gallery";
import Step4About from "./steps/Step4About";
import Step5Services from "./steps/Step5Services";
import Step6Pricing from "./steps/Step6Pricing";
import Step7Schedule from "./steps/Step7Schedule";
import Step8Contact from "./steps/Step8Contact";
import Step9Verification from "./steps/Step9Verification";
import Step10Preview from "./steps/Step10Preview";

/**
 * Premium 10-step Profile Wizard. Drop-in replacement for the old
 * ProfileWizard component — same props (role, profile, onClose, onSaved).
 */
export default function WizardShell({ role, profile, onClose, onSaved, currentUserId }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => hydrateForm(profile, role) || emptyForm(role));
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  // Restore localStorage draft on first mount if newer than the profile
  const profileId = profile?.id;
  const { status, savedAt, restoreDraft, clearLocalDraft, clearServerDraft } = useAutosave({
    profileId, ownerId: currentUserId, payload: form, step,
  });

  useEffect(() => {
    const draft = restoreDraft();
    if (!draft || !draft.payload) return;
    const profileTs = profile?.updated_at ? Date.parse(profile.updated_at) : 0;
    if (draft.ts && draft.ts > profileTs && typeof window !== "undefined") {
      if (window.confirm("We found an unsaved draft from a previous session. Restore it?")) {
        setForm((f) => ({ ...f, ...draft.payload }));
        if (typeof draft.step === "number") setStep(draft.step);
      } else {
        clearLocalDraft();
      }
    }
    // Only run once
  }, []);

  const current = WIZARD_STEPS[step];
  const total = WIZARD_STEPS.length;
  const progress = Math.round(((step + 1) / total) * 100);

  const validate = (k) => {
    const err = validateStep(k, form);
    setErrors((e) => ({ ...e, [k]: err }));
    return !err;
  };

  const next = () => {
    if (!validate(current.k)) return;
    setStep((s) => Math.min(s + 1, total - 1));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    // Validate every required step before publishing
    for (const s of WIZARD_STEPS) {
      const err = validateStep(s.k, form);
      if (err) {
        const i = WIZARD_STEPS.findIndex(x => x.k === s.k);
        setStep(i);
        setErrors((e) => ({ ...e, [s.k]: err }));
        toast.error(`${s.label}: ${err}`);
        return;
      }
    }
    setBusy(true);
    try {
      const payload = toApiPayload(form);
      const res = profile?.id
        ? await api.patch(`/profiles/${profile.id}`, payload)
        : await api.post(`/profiles`, payload);
      clearLocalDraft();
      await clearServerDraft();
      toast.success(profile?.id ? "Profile updated" : "Profile published");
      onSaved?.(res.data?.profile);
      onClose?.();
    } catch (e) {
      console.warn("[WizardShell] submit error:", e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Quick-save the current step without navigating to step 10. Validates ONLY
   * the active step so a user editing a single field doesn't have to walk
   * through the entire wizard. Keeps the modal open after saving.
   */
  const saveNow = async () => {
    if (!validate(current.k)) return;
    setBusy(true);
    try {
      const payload = toApiPayload(form);
      const res = profile?.id
        ? await api.patch(`/profiles/${profile.id}`, payload)
        : await api.post(`/profiles`, payload);
      // For newly-created profiles, we keep the modal open but stash the new id
      // so subsequent saves PATCH instead of POSTing duplicates.
      const saved = res.data?.profile;
      if (saved?.id && !profile?.id) {
        // Mutate the local profile reference so future saves know its id.
        // (WizardShell doesn't own profile state, but onSaved bubbles it up.)
        profile = saved;
      }
      clearLocalDraft();
      await clearServerDraft();
      toast.success("Saved successfully");
      onSaved?.(saved);
    } catch (e) {
      console.warn("[WizardShell] saveNow error:", e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const StepView = useMemo(() => {
    switch (current.k) {
      case "basic":        return <Step1Basic form={form} set={set} role={role} />;
      case "appearance":   return <Step2Appearance form={form} set={set} />;
      case "gallery":      return <Step3Gallery form={form} set={set} />;
      case "about":        return <Step4About form={form} set={set} />;
      case "services":     return <Step5Services form={form} set={set} />;
      case "pricing":      return <Step6Pricing form={form} set={set} />;
      case "schedule":     return <Step7Schedule form={form} set={set} />;
      case "contact":      return <Step8Contact form={form} set={set} />;
      case "verification": return <Step9Verification profile={profile} />;
      case "preview":      return <Step10Preview form={form} />;
      default: return null;
    }
  }, [current.k, form, set, role, profile]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-4" data-testid="profile-wizard">
      <div className="w-full sm:max-w-4xl sm:max-h-[92vh] h-full sm:h-auto bg-stone-950 border border-stone-800 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">

        {/* HEADER */}
        <div className="flex-none border-b border-stone-800 bg-stone-950/95 backdrop-blur supports-[backdrop-filter]:bg-stone-950/80">
          <div className="flex items-center justify-between px-3 sm:px-5 py-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-amber-400">Profile wizard</p>
              <h2 className="text-sm sm:text-base font-medium text-stone-100 truncate">
                Step {step + 1} of {total} · <span className="text-stone-400">{current.label}</span>
              </h2>
            </div>
            <div className="flex items-center gap-1.5">
              <AutosaveBadge status={status} savedAt={savedAt} hasProfile={!!profileId} />
              <button
                onClick={onClose}
                aria-label="Close"
                className="p-2 rounded-lg hover:bg-stone-900 text-stone-400 hover:text-stone-100"
                data-testid="wizard-close"
              ><X className="w-4 h-4" /></button>
            </div>
          </div>
          {/* progress bar */}
          <div className="h-0.5 bg-stone-900">
            <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all"
                 style={{ width: `${progress}%` }} />
          </div>
          {/* step pills — scrollable on mobile */}
          <div className="px-3 sm:px-5 py-2 overflow-x-auto scrollbar-thin">
            <div className="flex items-center gap-1.5 w-max">
              {WIZARD_STEPS.map((s, i) => {
                const done = i < step;
                const active = i === step;
                return (
                  <button
                    key={s.k}
                    onClick={() => setStep(i)}
                    data-testid={`wizard-step-${s.k}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium border whitespace-nowrap transition
                      ${active ? "bg-amber-500 text-stone-950 border-amber-500"
                        : done ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
                        : "bg-stone-900 text-stone-400 border-stone-700 hover:text-stone-200"}`}
                  >
                    <span className="hidden sm:inline">{i + 1}. </span>{s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-5">
          {errors[current.k] && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 text-xs" data-testid={`wizard-error-${current.k}`}>
              <AlertCircle className="w-4 h-4 flex-none mt-0.5" />
              <span>{errors[current.k]}</span>
            </div>
          )}
          {StepView}
        </div>

        {/* FOOTER — pb-16 on mobile keeps the action buttons clear of the
            Emergent preview badge (and any future floating CTA). */}
        <div className="flex-none border-t border-stone-800 bg-stone-950 p-3 pb-16 sm:p-4 sm:pb-4 flex items-center justify-between gap-2 relative z-10">
          <button
            onClick={prev}
            disabled={step === 0}
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 h-10 rounded-lg border border-stone-700 text-stone-200 hover:bg-stone-900 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
            data-testid="wizard-back"
          ><ChevronLeft className="w-4 h-4" /> Back</button>

          {/* Save — always available on every step. Validates ONLY the current
              step so the user can save a single field edit without walking the
              whole wizard. */}
          <button
            onClick={saveNow}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 h-10 rounded-lg border border-emerald-500/50 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50 text-sm"
            data-testid="wizard-save"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            <span className="hidden sm:inline">Save</span>
          </button>

          {step < total - 1 ? (
            <button
              onClick={next}
              className="inline-flex items-center gap-1.5 px-4 sm:px-5 h-10 rounded-lg bg-amber-500 text-stone-950 font-semibold hover:bg-amber-400 text-sm"
              data-testid="wizard-next"
            >Next <ChevronRight className="w-4 h-4" /></button>
          ) : (
            <button
              onClick={submit}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 sm:px-5 h-10 rounded-lg bg-amber-500 text-stone-950 font-semibold hover:bg-amber-400 disabled:opacity-60 text-sm"
              data-testid="wizard-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {profile?.id ? "Save profile" : "Publish profile"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AutosaveBadge({ status, savedAt, hasProfile }) {
  if (!hasProfile) return (
    <span className="text-[10px] text-stone-500 hidden sm:inline">Draft saved locally</span>
  );
  if (status === "saving") return (
    <span className="text-[10px] text-stone-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
  );
  if (status === "saved") return (
    <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1" title={savedAt}><CloudCheck className="w-3 h-3" /> Draft saved</span>
  );
  if (status === "error") return (
    <span className="text-[10px] text-rose-300 inline-flex items-center gap-1"><CloudOff className="w-3 h-3" /> Save error</span>
  );
  return null;
}
